import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserIcon, BuildingOfficeIcon, PhoneIcon, GlobeAltIcon, MapPinIcon, PhotoIcon, LinkIcon, MagnifyingGlassIcon, CheckIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { getVenueUserProfile, updateCurrentUser, softDeleteUserFromVenue} from '../Services/UserService.ts';
import { updateVenue } from '../Services/VenueService.ts';
import Helper from '../helpers';
import PopupModelWrapper from '../components/PopupModelWrapper';
import DeleteAccountModal from '../components/DeleteAccountModal';
import BeaconsReferralWidget from '../components/BeaconsReferralWidget';
import CountryCitySelect from '../components/CountryCitySelect';
import config from '../config';

const baseUrl = config.apiBaseUrl;

const SERVICE_OPTIONS = [
    { key: 'diet_meal_plans', label: 'Diet / Meal Plans' },
    { key: 'sports_massage', label: 'Sports Massage' },
    { key: 'consultations', label: 'Consultations' },
    { key: 'online_coaching', label: 'Online Coaching' },
    { key: 'group_sessions', label: 'Group Sessions' },
    { key: 'nutrition_advice', label: 'Nutrition Advice' },
    { key: 'body_composition', label: 'Body Composition Testing' },
    { key: 'rehabilitation', label: 'Rehabilitation / Injury Support' },
    { key: 'pre_post_natal', label: 'Pre / Post Natal' },
    { key: 'mindset_coaching', label: 'Mindset & Wellbeing Coaching' },
    { key: 'strength_conditioning', label: 'Strength & Conditioning' },
    { key: 'youth_training', label: 'Youth / Junior Training' },
];

const Profile = () => {
    // User fields
    const [firstName, setFirstName] = useState('');
    const [email, setEmail] = useState('');
    const [showOnLeaderboard, setShowOnLeaderboard] = useState(false);
    const [leaderboardDisplayName, setLeaderboardDisplayName] = useState('');
    const [userId, setUserId] = useState('');
    const [venueKey, setVenueKey] = useState('');
    
    // Admin/Venue fields
    const [isAdmin, setIsAdmin] = useState(false);
    const [venueName, setVenueName] = useState('');
    const [phone, setPhone] = useState('');
    const [website, setWebsite] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [address, setAddress] = useState({
        line1: '',
        line2: '',
        city: '',
        county: '',
        postcode: '',
        country: 'United Kingdom'
    });
    
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(true);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const navigate = useNavigate();

    // PT Directory listing state (available to both directory users and venue admins)
    const [directoryForm, setDirectoryForm] = useState({});
    const [directoryServices, setDirectoryServices] = useState([]);
    const [directorySaving, setDirectorySaving] = useState(false);
    const [directoryMsg, setDirectoryMsg] = useState('');
    const [showDirectorySection, setShowDirectorySection] = useState(false);

    const fetchDirectoryListing = useCallback(async () => {
        try {
            const token = await Helper.getCookie('wh-jwt');
            const res = await fetch(`${baseUrl}/api/ptdirectory/my-listing`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                if (data.listing) {
                    setDirectoryForm({
                        name: data.listing.name || '',
                        city: data.listing.city || '',
                        postcode: data.listing.postcode || '',
                        bio: data.listing.bio || '',
                        specialisms: data.listing.specialisms || '',
                        priceType: data.listing.priceType || 'from',
                        priceFrom: data.listing.priceFrom || '',
                        priceTo: data.listing.priceTo || '',
                        beaconsUsername: data.listing.beaconsUsername || '',
                        instagramUrl: data.listing.instagramUrl || '',
                        tikTokUrl: data.listing.tikTokUrl || '',
                        facebookUrl: data.listing.facebookUrl || '',
                        stravaUrl: data.listing.stravaUrl || '',
                        blueskyUrl: data.listing.blueskyUrl || '',
                        linkedInUrl: data.listing.linkedInUrl || '',
                    });
                    setDirectoryServices(
                        data.listing.services
                            ? data.listing.services.split(',').map(s => s.trim()).filter(Boolean)
                            : []
                    );
                    setShowDirectorySection(true);
                }
            }
        } catch (err) {
            console.error('Error fetching directory listing', err);
        }
    }, []);

    const handleSaveDirectory = async () => {
        setDirectorySaving(true);
        setDirectoryMsg('');
        try {
            const token = await Helper.getCookie('wh-jwt');
            const payload = {
                ...directoryForm,
                priceFrom: directoryForm.priceFrom ? parseFloat(directoryForm.priceFrom) : null,
                priceTo: directoryForm.priceType === 'range' && directoryForm.priceTo ? parseFloat(directoryForm.priceTo) : null,
                services: directoryServices.join(','),
            };
            const res = await fetch(`${baseUrl}/api/ptdirectory/my-listing`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload)
            });
            setDirectoryMsg(res.ok ? 'Directory listing saved!' : 'Failed to save listing.');
            if (res.ok) setTimeout(() => setDirectoryMsg(''), 3000);
        } catch {
            setDirectoryMsg('Failed to save listing.');
        } finally {
            setDirectorySaving(false);
        }
    };

    const toggleDirectoryService = (key) => {
        setDirectoryServices(prev =>
            prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]
        );
    };

    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                setLoading(true);
                
                // Fetch current user information
                const venue = await Helper.getUserVenueId();
                setVenueKey(venue);
                var user = await getVenueUserProfile(venue);

                // Set user data
                setFirstName(user.name || '');
                setEmail(user.email || '');
                setUserId(user.userId || '');
                setShowOnLeaderboard(user.showOnLeaderboard || false);
                setLeaderboardDisplayName(user.leaderboardDisplayName || '');
                const admin = await Helper.userIsAdmin();
                setIsAdmin(admin);

                // Always try to fetch directory listing (available to admins and PT-directory users)
                await fetchDirectoryListing();

            } catch (error) {
                console.error('Error fetching user info:', error);
                setError('Failed to load user information');
            } finally {
                setLoading(false);
            }
        };

        fetchUserInfo();
    }, [fetchDirectoryListing]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            // Update user properties
            const userUpdateData = {
                firstName,
                email,
                showOnLeaderboard,
                leaderboardDisplayName: leaderboardDisplayName || null
            };
            
            await updateCurrentUser(userUpdateData);
            
            // Update venue data if admin
            if (isAdmin) {
                const venueUpdateData = {
                    name: venueName,
                    phone,
                    website,
                    logoUrl,
                    address: {
                        line1: address.line1,
                        line2: address.line2,
                        city: address.city,
                        county: address.county,
                        postcode: address.postcode,
                        country: address.country
                    }
                };
                
                await updateVenue(venueUpdateData);
            }

            setSuccess('Profile updated successfully');
        } catch (error) {
            console.error('Error updating profile:', error);
            setError('Failed to update profile. Please try again.');
        }
    };

    const handleOpenDeleteModal = () => {
        setOpenDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setOpenDeleteModal(false);
    };

    const handleDeleteAccount = async () => {
        try {
            await softDeleteUserFromVenue(venueKey, userId);
            alert('Your account has been deleted from this venue.');
            // Redirect to logout or home page
            navigate('/logout');
        } catch (error) {
            console.error('Error deleting account:', error);
            alert('Failed to delete account. Please try again.');
        }
    };

    const handleAddressChange = (field, value) => {
        setAddress(prev => ({
            ...prev,
            [field]: value
        }));
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-black py-8 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <div className="animate-pulse">
                        <div className="bg-gray-800 h-8 rounded w-1/4 mb-8"></div>
                        <div className="card p-6 space-y-6">
                            <div className="bg-gray-700 h-6 rounded w-1/3"></div>
                            <div className="space-y-4">
                                <div className="bg-gray-700 h-10 rounded"></div>
                                <div className="bg-gray-700 h-10 rounded"></div>
                                <div className="bg-gray-700 h-10 rounded"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen  py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-white">Profile Settings</h1>
                    <p className="text-gray-400 mt-2">Manage your account</p>
                </div>

                {error && (
                    <div className="bg-red-900/20 border border-red-800/30 text-red-300 px-4 py-3 rounded-lg mb-6">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="bg-emerald-900/20 border border-emerald-800/30 text-emerald-300 px-4 py-3 rounded-lg mb-6">
                        {success}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Personal Information */}
                    <div className="card p-6">
                        <div className="flex items-center mb-6">
                            <UserIcon className="h-6 w-6 text-emerald-400 mr-3" />
                            <h2 className="text-xl font-semibold text-white">Personal Information</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Name *
                                </label>
                                <input
                                    type="text"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required
                                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                    placeholder="Enter your first name"
                                />
                            </div>
                            
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Email Address *
                                </label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                    placeholder="Enter your email address"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Leaderboard Privacy Settings */}
                    {false && (
                    <div className="card p-6">
                        <div className="flex items-center mb-6">
                            <svg className="h-6 w-6 text-emerald-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            <h2 className="text-xl font-semibold text-white">Leaderboard & Privacy</h2>
                        </div>
                        
                        <div className="space-y-6">
                            <div className="flex items-start">
                                <div className="flex items-center h-5">
                                    <input
                                        type="checkbox"
                                        id="showOnLeaderboard"
                                        checked={showOnLeaderboard}
                                        onChange={(e) => setShowOnLeaderboard(e.target.checked)}
                                        className="h-4 w-4 text-emerald-600 bg-gray-700 border-gray-600 rounded focus:ring-emerald-500"
                                    />
                                </div>
                                <div className="ml-3 text-sm">
                                    <label htmlFor="showOnLeaderboard" className="font-medium text-white">
                                        Show me on training plan leaderboards
                                    </label>
                                    <p className="text-gray-400 mt-1">
                                        When enabled, your progress will appear on venue leaderboards for training plans. 
                                        Only trainers/admins can see the leaderboard.
                                    </p>
                                </div>
                            </div>

                            {showOnLeaderboard && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Leaderboard Display Name (optional)
                                    </label>
                                    <input
                                        type="text"
                                        value={leaderboardDisplayName}
                                        onChange={(e) => setLeaderboardDisplayName(e.target.value)}
                                        maxLength={100}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                        placeholder="Leave blank to use your real name"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">
                                        Choose a custom name to appear on the leaderboard, or leave blank to use your real name ({firstName}).
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>)}

                    {/* Venue Information - Only show for admins */}
                    {isAdmin && (
                        <>
                            {false && (
                            <div className="card p-6">
                                <div className="flex items-center mb-6">
                                    <BuildingOfficeIcon className="h-6 w-6 text-emerald-400 mr-3" />
                                    <h2 className="text-xl font-semibold text-white">Venue Information</h2>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Venue Name
                                        </label>
                                        <input
                                            type="text"
                                            value={venueName}
                                            onChange={(e) => setVenueName(e.target.value)}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                            placeholder="Enter venue name"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            <PhoneIcon className="h-4 w-4 inline mr-1" />
                                            Phone Number
                                        </label>
                                        <input
                                            type="tel"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                            placeholder="Enter phone number"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            <GlobeAltIcon className="h-4 w-4 inline mr-1" />
                                            Website
                                        </label>
                                        <input
                                            type="url"
                                            value={website}
                                            onChange={(e) => setWebsite(e.target.value)}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                            placeholder="https://example.com"
                                        />
                                    </div>
                                    
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            <PhotoIcon className="h-4 w-4 inline mr-1" />
                                            Logo URL
                                        </label>
                                        <input
                                            type="url"
                                            value={logoUrl}
                                            onChange={(e) => setLogoUrl(e.target.value)}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                            placeholder="https://example.com/logo.png"
                                        />
                                    </div>
                                </div>
                            </div>
                            )}
                            
                            {/* Address Information */}
                            {false && (<div className="card p-6">
                                <div className="flex items-center mb-6">
                                    <MapPinIcon className="h-6 w-6 text-emerald-400 mr-3" />
                                    <h2 className="text-xl font-semibold text-white">Address Information</h2>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Address Line 1
                                        </label>
                                        <input
                                            type="text"
                                            value={address.line1}
                                            onChange={(e) => handleAddressChange('line1', e.target.value)}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                            placeholder="Street address"
                                        />
                                    </div>
                                    
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Address Line 2
                                        </label>
                                        <input
                                            type="text"
                                            value={address.line2}
                                            onChange={(e) => handleAddressChange('line2', e.target.value)}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                            placeholder="Apartment, suite, etc. (optional)"
                                        />
                                    </div>
                                    
                                    <CountryCitySelect
                                        country={address.country}
                                        city={address.city}
                                        onCountryChange={(val) => handleAddressChange('country', val)}
                                        onCityChange={(val) => handleAddressChange('city', val)}
                                        fieldClass="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                        labelClass="block text-sm font-medium text-gray-300 mb-2"
                                    />
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            County
                                        </label>
                                        <input
                                            type="text"
                                            value={address.county}
                                            onChange={(e) => handleAddressChange('county', e.target.value)}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                            placeholder="County"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Postcode
                                        </label>
                                        <input
                                            type="text"
                                            value={address.postcode}
                                            onChange={(e) => handleAddressChange('postcode', e.target.value)}
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                            placeholder="Postcode"
                                        />
                                    </div>
                                </div>
                            </div>
                            )}
                        </>
                    )}

                    {/* Beacons.ai Social Media & Referrals - show for venue admins */}
                    {isAdmin && (
                        <div>
                            <div className="flex items-center mb-2 px-1">
                                <LinkIcon className="h-5 w-5 text-emerald-400 mr-2" />
                                <h2 className="text-xl font-semibold text-white">Social Media & Referrals</h2>
                            </div>
                            <BeaconsReferralWidget isAdmin={true} />
                        </div>
                    )}

                    {/* PT Directory Listing – visible when user has an active listing */}
                    {isAdmin && showDirectorySection && (
                        <div className="card p-6">
                            <div className="flex items-center mb-5">
                                <MagnifyingGlassIcon className="h-6 w-6 text-amber-400 mr-3" />
                                <div>
                                    <h2 className="text-xl font-semibold text-white">PT Directory Listing</h2>
                                    <p className="text-gray-400 text-sm mt-0.5">
                                        Edit how you appear in the WorldsHighStreet PT Directory.
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Display Name</label>
                                    <input value={directoryForm.name || ''} onChange={e => setDirectoryForm(p => ({...p, name: e.target.value}))} className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Your display name" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Postcode</label>
                                    <input value={directoryForm.postcode || ''} onChange={e => setDirectoryForm(p => ({...p, postcode: e.target.value}))} className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="e.g. SW1A 1AA" />
                                </div>
                                <div className="md:col-span-2">
                                    <CountryCitySelect
                                        country={directoryForm.country || 'United Kingdom'}
                                        city={directoryForm.city || ''}
                                        onCountryChange={val => setDirectoryForm(p => ({...p, country: val}))}
                                        onCityChange={val => setDirectoryForm(p => ({...p, city: val}))}
                                        cityLabel="City"
                                        fieldClass="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                        labelClass="block text-sm font-medium text-gray-300 mb-2"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Bio</label>
                                    <textarea value={directoryForm.bio || ''} onChange={e => setDirectoryForm(p => ({...p, bio: e.target.value}))} rows={3} className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" placeholder="Tell potential clients about yourself..." />
                                </div>
                            </div>

                            {/* Pricing */}
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-300 mb-2">Session Pricing</label>
                                <div className="flex gap-3 flex-wrap">
                                    <select value={directoryForm.priceType || 'from'} onChange={e => setDirectoryForm(p => ({...p, priceType: e.target.value}))} className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm">
                                        <option value="from">From price</option>
                                        <option value="range">Price range</option>
                                    </select>
                                    <input type="number" value={directoryForm.priceFrom || ''} onChange={e => setDirectoryForm(p => ({...p, priceFrom: e.target.value}))} className="w-28 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm" placeholder="£ from" />
                                    {directoryForm.priceType === 'range' && (
                                        <input type="number" value={directoryForm.priceTo || ''} onChange={e => setDirectoryForm(p => ({...p, priceTo: e.target.value}))} className="w-28 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm" placeholder="£ to" />
                                    )}
                                </div>
                            </div>

                            {/* Services */}
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-1"><WrenchScrewdriverIcon className="h-4 w-4" /> Services Offered</label>
                                <div className="flex flex-wrap gap-2">
                                    {SERVICE_OPTIONS.map(({ key, label }) => (
                                        <button key={key} type="button" onClick={() => toggleDirectoryService(key)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${directoryServices.includes(key) ? 'border-emerald-500 bg-emerald-900/30 text-emerald-300' : 'border-gray-600 text-gray-400 hover:border-gray-400'}`}>
                                            {directoryServices.includes(key) && <CheckIcon className="h-3 w-3 inline mr-1" />}{label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-5 flex items-center gap-3">
                                <button type="button" onClick={handleSaveDirectory} disabled={directorySaving} className="px-5 py-2 bg-amber-500 hover:bg-amber-400 text-gray-900 font-semibold rounded-lg transition-colors flex items-center gap-2">
                                    {directorySaving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div> : <CheckIcon className="h-4 w-4" />}
                                    {directorySaving ? 'Saving...' : 'Save Directory Listing'}
                                </button>
                                {directoryMsg && <span className={`text-sm ${directoryMsg.includes('saved') ? 'text-emerald-400' : 'text-red-400'}`}>{directoryMsg}</span>}
                            </div>
                        </div>
                    )}

                    {/* Danger Zone - Delete Account */}
                    <div className="card p-6 border-2 border-red-500">
                        <div className="flex items-center mb-4">
                            <svg className="h-6 w-6 text-red-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <h2 className="text-xl font-semibold text-white">Danger Zone</h2>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="space-y-3">
                                <p className="text-gray-300">
                                    Deleting your account will remove you from this venue. This action cannot be undone.
                                </p>
                                <p className="text-gray-300">
                                    <strong className="text-yellow-400">Important:</strong> Deleting your account from this venue does not terminate your use of Worlds High Street services and does not affect any outstanding contractual obligations you may have with your Venue or Personal Trainer.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={handleOpenDeleteModal}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                            >
                                Delete My Account
                            </button>
                        </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end">
                        <button
                            type="submit"
                            className="btn-primary"
                        >
                            Update Profile
                        </button>
                    </div>
                </form>
                
                {/* Delete Account Modal */}
                <PopupModelWrapper 
                    isOpen={openDeleteModal} 
                    handleClose={handleCloseDeleteModal}
                    ModelContent={DeleteAccountModal} 
                    Values={{userName: firstName, onConfirm: handleDeleteAccount}}
                />
            </div>
        </div>
    );
};

export default Profile;
